/**
 * Shared vocabulary for the "is this source connected?" question every
 * provider-backed feed has to answer. `SOURCE_CONNECTION_REGISTRY` below is
 * derived from `providerRegistry.ts`'s `SOURCE_PROVIDERS` — the single place
 * that describes a connectable source for the SPA; adding a new provider
 * means adding one entry there, not a new hand-rolled empty/error screen.
 */

import { CONNECTABLE_SOURCES, type ConnectableSource } from './providerIds';
import { SOURCE_PROVIDERS } from './providerRegistry';

export { CONNECTABLE_SOURCES, type ConnectableSource };

/**
 * Normalised connection status shared by Jira/GitHub acks and the Slack probe.
 * `rejected` = the provider explicitly refused the stored credential (expired / revoked).
 */
export type SourceConnectionStatus = 'ok' | 'not_configured' | 'error' | 'rejected' | 'mock';

export interface SourceConnectionConfig {
  id: ConnectableSource;
  /** Human provider name ("Jira"). */
  label: string;
  /** What the feed shows once connected ("tus tickets"). */
  noun: string;
  /** One-line value proposition shown on the connection panel. */
  description: string;
  /** Copy when the provider is connected but the feed has no items. */
  emptyMessage: string;
  /** Copy when items exist but the active filter hides all of them. */
  filterEmptyMessage: string;
  /** Settings section the deep link opens (the provider accordion). */
  settingsSection: ConnectableSource;
  skeletonHeight: number;
}

export const SOURCE_CONNECTION_REGISTRY: Record<ConnectableSource, SourceConnectionConfig> =
  Object.fromEntries(
    CONNECTABLE_SOURCES.map((id) => {
      const def = SOURCE_PROVIDERS[id];
      const config: SourceConnectionConfig = {
        id,
        label: def.label,
        noun: def.noun,
        description: def.description,
        emptyMessage: def.emptyMessage,
        filterEmptyMessage: def.filterEmptyMessage,
        settingsSection: id,
        skeletonHeight: def.skeletonHeight,
      };
      return [id, config];
    }),
  ) as Record<ConnectableSource, SourceConnectionConfig>;

export function sourceConnectionConfig(source: ConnectableSource): SourceConnectionConfig {
  return SOURCE_CONNECTION_REGISTRY[source];
}

export function sourceLabel(source: ConnectableSource): string {
  return SOURCE_CONNECTION_REGISTRY[source].label;
}

/** Title of the connection panel. */
export function sourceNotConnectedTitle(source: ConnectableSource): string {
  return `${sourceLabel(source)} no está conectado`;
}

/** Single not-configured template used by error mappers and the connection panel. */
export function sourceNotConfiguredMessage(source: ConnectableSource): string {
  const { label, noun } = SOURCE_CONNECTION_REGISTRY[source];
  return `${label} no está configurado. Abre Settings → ${label} y conecta tu cuenta para ver ${noun}.`;
}

export function openSettingsActionLabel(source: ConnectableSource): string {
  return `Conectar ${sourceLabel(source)}`;
}

/** Title of the connection panel when the provider rejected the stored credential. */
export function sourceCredentialsRejectedTitle(source: ConnectableSource): string {
  return `El token de ${sourceLabel(source)} fue rechazado`;
}

/** Single credentials-rejected template used by error mappers and the gate. */
export function sourceCredentialsRejectedMessage(source: ConnectableSource): string {
  const { label } = SOURCE_CONNECTION_REGISTRY[source];
  return `${label} rechazó el token guardado (expirado o revocado). Genera uno nuevo y guárdalo en Settings → ${label}.`;
}

/**
 * Detects a rejected-credential load error: the canonical template, the BFF
 * `*_AUTH_REJECTED` codes, or provider auth failures that leaked through.
 */
export function isSourceCredentialsRejectedMessage(message: string | null | undefined): boolean {
  if (!message) return false;
  if (/_AUTH_REJECTED\b/.test(message)) return true;
  if (/rechazó el token guardado/i.test(message)) return true;
  return isProviderAuthFailureText(message);
}

/** Provider wording for an expired / revoked / invalid credential (never scopes or rate limits). */
export function isProviderAuthFailureText(message: string | null | undefined): boolean {
  if (!message) return false;
  // A bare "401" is not enough: issue keys (PROJ-401) and PR numbers (#401) would match.
  return /\bHTTP[\s/]*401\b|\bstatus(?:\s+code)?\s*[:=]?\s*401\b|\(401\)|unauthori[sz]ed|bad credentials|client must be authenticated|invalid_auth|token_revoked|token_expired|account_inactive|not_authed/i.test(
    message,
  );
}

/**
 * Detects a not-configured load error from its user-facing message. Covers
 * the canonical template, raw BFF codes that may leak through (`*_NOT_CONFIGURED`,
 * `*_TOKEN_MISSING`) and English BFF copy, but never auth/scope/rate-limit
 * failures — those need the retry-oriented ErrorState instead.
 */
export function isSourceNotConfiguredMessage(message: string | null | undefined): boolean {
  if (!message) return false;
  if (/_(NOT_CONFIGURED|TOKEN_MISSING)\b/.test(message)) return true;
  if (/no está configurad/i.test(message)) return true;
  return /\bnot configured\b/i.test(message);
}

/**
 * True when a load error's copy already tells the user to go to Settings
 * (auth / scope failures). Such errors keep the red ErrorState but must also
 * offer the Settings deep link.
 */
export function mentionsSettings(message: string | null | undefined): boolean {
  if (!message) return false;
  return /Settings|Configuraci[oó]n|configur/i.test(message);
}

export function normalizeConnectionStatus(status: unknown): SourceConnectionStatus {
  if (
    status === 'ok' ||
    status === 'not_configured' ||
    status === 'mock' ||
    status === 'rejected'
  ) {
    return status;
  }
  return 'error';
}

/**
 * Turn a raw ack/probe into a verdict: an `error` whose detail is a provider
 * auth failure is `rejected`.
 */
export function classifyConnectionProbe(input: {
  status: unknown;
  message?: string | null;
}): SourceConnectionStatus {
  const status = normalizeConnectionStatus(input.status);
  if (status === 'error' && isProviderAuthFailureText(input.message)) return 'rejected';
  return status;
}

/** Feed-level verdict derived from the load error and the connection probe. */
export type SourceGateState =
  'checking' | 'not_configured' | 'credentials_rejected' | 'probe_error' | 'ready';

export function resolveSourceGate(input: {
  error: string | null;
  isLoading: boolean;
  totalCount: number;
  probe: { status: SourceConnectionStatus; message: string | null } | null;
}): SourceGateState {
  if (isSourceNotConfiguredMessage(input.error)) return 'not_configured';
  if (isSourceCredentialsRejectedMessage(input.error)) return 'credentials_rejected';
  if (input.error || input.isLoading || input.totalCount > 0) return 'ready';
  if (!input.probe) return 'checking';
  if (input.probe.status === 'not_configured') return 'not_configured';
  if (input.probe.status === 'rejected') return 'credentials_rejected';
  if (input.probe.status === 'error') return 'probe_error';
  return 'ready';
}

/** Days until `expiresAt` (negative when already expired); null when unknown. */
export function daysUntil(
  expiresAt: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!expiresAt) return null;
  const ms = Date.parse(expiresAt);
  if (!Number.isFinite(ms)) return null;
  return Math.ceil((ms - now) / 86_400_000) + 0; // `+ 0` folds -0 into 0
}
